import { Result } from '@/types/response';
import axios from '../utils/axiosInstance';
import type {
  Preceptor,
  PreceptorProfile,
  PreceptorSiteAssignment,
  PreceptorSiteSchedule,
} from '@/types/preceptor';
import { Paginate } from '@/types/paginate';
import { AdminPreceptorStatus } from '@/modules/admin/components/preceptors-table';
import { Label } from '@/lib/constants/labels';
import type { PreceptorBooking, PreceptorBookingDetail } from '@/types/api/booking';
import type { ImportJobProgress, CommitJobProgress } from '@/store/import-job-store';
import type { StudentBookingDocumentsResponse } from '@/services/student-service';

/**
 * Fetch all documents attached to a preceptor's bookings, grouped by booking/rotation.
 * Shares the response shape with the student equivalent so the same UI can render both.
 * (Distinct from `getPreceptorBookingDocuments`, which fetches docs for a single booking.)
 */
export async function getPreceptorRotationDocuments(
  preceptorId: number | string,
): Promise<StudentBookingDocumentsResponse> {
  const res = await axios.get(
    `/admin/preceptors/${encodeURIComponent(String(preceptorId))}/booking-documents`,
  );
  return {
    data: Array.isArray(res.data?.data) ? res.data.data : [],
    total_documents: res.data?.total_documents ?? 0,
  };
}

export interface PreceptorSearchParams {
  q?: string;
  specialty?: string | number;
  start?: string;
  end?: string;
  location?: string;
}
 
export async function searchPreceptors(params: PreceptorSearchParams): Promise<Result<Preceptor[]>> {
  const qs = new URLSearchParams();
  (Object.entries(params) as [keyof PreceptorSearchParams, string | undefined][]).forEach(([key, value]) => {
    if (value) qs.append(key, value);
  });
  const url = `/preceptors${qs.size ? `?${qs.toString()}` : ''}`;
  const response = await axios.get<Result<Preceptor[]>>(url);
  return response.data;
}

export async function getPreceptor(id: string): Promise<Preceptor> {
  return axios.get<Result<Preceptor>>(`/preceptors/${encodeURIComponent(id)}`).then(res => res.data.data);
}


export async function getPendingPreceptor() {
  return axios.get<Result<number>>(`/preceptors/pending`).then(res => res.data.data);
}

export interface PreceptorAdminFilters {
  status?: AdminPreceptorStatus | 'all';
  search?: string;
  /** Specialty ids to filter by (matches any of them). */
  specialties?: Array<string | number>;
  /** Two-letter state codes of an associated site (matches any of them). */
  states?: string[];
  /** Partial, case-insensitive match against an associated site's city. */
  city?: string;
  /** Partial, case-insensitive match against the rotation code. */
  rotationCode?: string;
  /** Column to sort the whole result set by (server-side). */
  sortBy?: 'name' | 'rotationCode' | 'status' | 'createdAt';
  sortDir?: 'asc' | 'desc';
}

export interface PreceptorStats {
  verified: number;
  unverified: number;
}

export interface PaginatedPreceptorsResponse extends Paginate<BackendPreceptor[]> {
  stats?: PreceptorStats;
}

export async function getPaginatedPreceptors(
  page: number = 1,
  perPage: number = 15,
  filters: PreceptorAdminFilters = {},
): Promise<PaginatedPreceptorsResponse> {
  const params: Record<string, unknown> = { page, per_page: perPage };
  const trimmedSearch = filters.search?.trim();
  const normalizedStatus = filters.status;

  if (trimmedSearch) {
    params.search = trimmedSearch;
  }

  if (normalizedStatus && normalizedStatus !== 'all') {
    params.status = normalizedStatus;
  }

  // Additional column filters — only sent when set so the backend keeps
  // returning the full list otherwise.
  if (filters.specialties && filters.specialties.length > 0) params.specialties = filters.specialties;
  if (filters.states && filters.states.length > 0) params.states = filters.states;
  if (filters.city?.trim()) params.city = filters.city.trim();
  if (filters.rotationCode?.trim()) params.rotation_code = filters.rotationCode.trim();

  // Sorting is applied server-side so A-Z orders every preceptor, not just the
  // rows on the current page.
  if (filters.sortBy) {
    params.sort_by = filters.sortBy;
    params.sort_dir = filters.sortDir ?? 'asc';
  }

  const res = await axios.get<{ data: BackendPreceptor[]; pagination: { current_page: number; per_page: number; total: number; last_page: number }; stats?: { verified: number; unverified: number }; message: string }>(
    `/admin/preceptors`,
    { params },
  );
  const payload = res.data;

  return {
    data: payload.data as BackendPreceptor[],
    links: {
      first: '',
      last: '',
      prev: null,
      next: null,
    },
    meta: {
      current_page: payload.pagination.current_page,
      from: payload.pagination.current_page === 1 ? 1 : (payload.pagination.current_page - 1) * payload.pagination.per_page + 1,
      last_page: payload.pagination.last_page,
      links: [],
      path: '',
      per_page: payload.pagination.per_page,
      to: Math.min(payload.pagination.total, payload.pagination.current_page * payload.pagination.per_page),
      total: payload.pagination.total,
    },
    stats: payload.stats,
  };
}

export async function getPreceptors(): Promise<Preceptor[]> {
  return axios.get<Result<Preceptor[]>>(`/public/preceptors`).then(res => res.data.data);
}

export interface BackendPreceptorSite {
  id: number;
  name: string;
  city: string;
  state: string;
  address?: string;
  zip: string;
  latitude: number | string | null;
  longitude: number | string | null;
  public_location?: {
    latitude: number | string;
    longitude: number | string;
  } | null;
  is_primary?: boolean;
  notes?: string | null;
}

export interface BackendPreceptor {
  id: string | number;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    user_type?: string;
  } | null;
  display_name?: string | null;
  primary_email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  titles?: string[] | null;
  specialty: {
    id: number;
    name: string;
  } | null;
  specialties?: Array<{
    id: number | string;
    name?: string;
  }>;
  specialty_id?: number | string | null;
  specialty_ids?: Array<number | string>;
  site: {
    id: number;
    name: string;
    city: string;
    state: string;
    address?: string;
    zip: string;
    latitude: number | string;
    longitude: number | string;
    public_location?: {
      latitude: number | string;
      longitude: number | string;
    } | null;
  } | null;
  /** Array of all sites associated with this preceptor */
  sites?: BackendPreceptorSite[];
  license_number?: string;
  license_state?: string;
  license_states?: string[];
  years_experience?: number;
  bio?: string;
  profile_photo?: string;
  status?: string;
  accepts_tele?: boolean;
  accepts_in_person?: boolean;
  accepted_program_types?: string | string[];
  preceptor_rate?: number | string | null;
  preceptor_rate_type?: string;
  student_rate?: number | string | null;
  student_rate_type?: string;
  honorarium_speed?: string;
  payment_method?: string;
  payment_note?: string | null;
  site_note?: string | null;
  average_rating?: number | string;
  total_ratings?: number;
  rotation_code?: string;
  rotation_setting?: string;
  seasons_available?: string | string[];
  seasons_unavailable?: string | string[];
  available_slots?: number;
  contacts?: Record<string, unknown>[];
  phone_contacts?: ContactPayload[] | null;
  email_contacts?: ContactPayload[] | null;
  letterhead_file?: {
    id: number;
    name: string;
    path: string;
  } | null;
  created_at?: string;
  updated_at?: string;
}

// Clerk pending preceptor response format
export interface ClerkPendingPreceptor {
  id: string;
  clerk_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  profile_photo: string | null;
  type: string | null;
  role: string;
  is_verified: boolean;
  onboarding_completed: boolean;
  created_at: string | null;
  updated_at: string | null;
  last_sign_in_at: string | null;
}

export interface GetAllPreceptorsParams {
  university_name?: string;
  /** Filter out preceptors unavailable for this start date (YYYY-MM-DD) */
  start_date?: string;
  /** Filter out preceptors unavailable for this end date (YYYY-MM-DD) */
  end_date?: string;
}

export async function getAllPreceptors(params?: GetAllPreceptorsParams): Promise<BackendPreceptor[]> {
  const queryParams: Record<string, string> = {};
  if (params?.university_name) {
    queryParams.university_name = params.university_name;
  }
  if (params?.start_date) {
    queryParams.start_date = params.start_date;
  }
  if (params?.end_date) {
    queryParams.end_date = params.end_date;
  }
  const res = await axios.get<{ data: BackendPreceptor[]; message: string }>(`/public/preceptors`, {
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  });
  return res.data.data;
}


export async function getPendingPreceptors(): Promise<ClerkPendingPreceptor[]> {
  const res = await axios.get<{ data: ClerkPendingPreceptor[]; message: string }>(`/admin/preceptors/preceptors-pending`);
  return res.data.data;
}

export async function updateStatus(id: string, status: AdminPreceptorStatus): Promise<Preceptor> {
  return axios.put<Result<Preceptor>>(`/preceptors/${encodeURIComponent(id)}/status`, { status }).then(res => res.data.data);
}

export async function verifyPreceptor(id: string, is_verified: boolean): Promise<void> {
  await axios.post(`/admin/preceptors/verify-preceptor/${encodeURIComponent(id)}`, { is_verified });
}

// Onboarding data returned when admin reviews a pending preceptor
export interface PendingPreceptorOnboardingData {
  // Clerk user info
  clerk_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  profile_photo: string | null;
  type: string | null;
  role: string;
  // Onboarding form data from setup-flow
  years_experience: number | null;
  license_state: string | null;
  license_states?: string[] | null;
  accepted_programs: AcceptedProgramPayload[];
  accepted_program_types: string[];
  accepts_tele: boolean;
  accepts_in_person: boolean;
  availability_notes: string | null;
  onboarding_completed: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export async function getPendingPreceptorOnboardingData(clerkId: string): Promise<PendingPreceptorOnboardingData> {
  const res = await axios.get<Result<PendingPreceptorOnboardingData>>(
    `/admin/preceptors/pending/${encodeURIComponent(clerkId)}/onboarding-data`
  );
  return res.data.data;
}

// Payload for admitting a pending preceptor (creating their account)
export interface AdminAdmitPendingPreceptorPayload {
  // Personal info
  first_name: string;
  last_name: string;
  email: string;
  // Site info (admin-only)
  site_name: string;
  site_address: string;
  site_city: string;
  site_state: string;
  site_zip: string;
  site_latitude: number;
  site_longitude: number;
  public_latitude?: number;
  public_longitude?: number;
  // Professional info
  specialty_ids: number[];
  license_number?: string;
  license_state?: string;
  license_states?: string[];
  years_experience?: number;
  // Program settings
  accepted_program_types: string[];
  accepted_programs?: AcceptedProgramPayload[];
  accepts_tele: boolean;
  accepts_in_person: boolean;
  // Rates (admin-only)
  preceptor_rate?: string;
  preceptor_rate_type?: 'flat' | 'hourly';
  student_rate?: string;
  student_rate_type?: 'flat' | 'hourly';
  honorarium_speed?: string;
  payment_method?: string;
  payment_instruction?: string;
  payment_note?: string;
  site_note?: string;
  // Schedule
  site_schedules?: Array<{
    day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
    start: string;
    end: string;
  }>;
  // Optional
  bio?: string;
  availability_notes?: string;
  rotation_setting?: string;
}

export interface AdminAdmitPendingPreceptorResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  preceptor: {
    id: number;
    status: string;
  };
  site: {
    id: number;
    name: string;
  };
}

export interface AdminCreatePreceptorPayload {
  /** When true (default), create a Pipedrive person for this preceptor. */
  sync_to_pipedrive?: boolean;
  site: {
    location_name: string;
    country_region?: string | null;
    unit_level?: string | null;
    building_name?: string | null;
    street_address: string;
    city_municipality: string;
    province: string;
    zip_code: string;
    latitude: number;
    longitude: number;
    public_location?: {
      latitude: number;
      longitude: number;
    } | null;
  };
  preceptor: {
    first_name: string;
    last_name: string;
    titles?: string[] | null;
    specialty_id?: number | null;
    specialty_ids: number[];
    email_contacts: ContactPayload[];
    phone_contacts?: ContactPayload[];
    status?: 'inactive';
    license_number?: string | null;
    license_state?: string | null;
    license_states?: string[] | null;
    license_issue_date?: string | null;
    license_expiration_date?: string | null;
    license_verification_url?: string | null;
    cv_link?: string | null;
    certificate_link?: string | null;
    years_experience?: number | null;
    bio?: string | null;
    current_position?: string | null;
    accepted_program_types?: string[] | null;
    accepted_programs?: AcceptedProgramPayload[] | null;
    accepts_tele?: boolean;
    accepts_in_person?: boolean;
    preceptor_rate?: number | null;
    preceptor_rate_type?: 'flat' | 'hourly' | null;
    student_rate?: number | null;
    student_rate_type?: 'flat' | 'hourly' | null;
    honorarium_speed?: string | null;
    payment_method?: string | null;
    payment_instruction?: string | null;
    payment_note?: string | null;
    site_note?: string | null;
    rotation_setting?: string | null;
    schedule?: Array<{
      day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
      start?: string | null;
      end?: string | null;
      timezone?: string | null;
    }>;
    certifications?: Array<{
      name: string;
      issuer: string;
      issue_date: string;
      expiration_date: string;
      number: string;
    }> | null;
  };
}

export type AdminPreceptorImportRowStatus = 'ready' | 'duplicate' | 'error';

export interface AdminPreceptorImportEditableFields {
  first_name: string;
  last_name: string;
  titles: string;
  primary_email: string;
  specialties: string;
  site_name: string;
  site_address: string;
  seasons_available: string;
  payment_note: string;
  site_note: string;
  /** Mapbox-resolved coordinates — passed to backend to skip Nominatim geocoding */
  site_latitude?: number;
  site_longitude?: number;
}

export interface AdminPreceptorImportRawField {
  label: string;
  value: string | null;
}

export interface AdminPreceptorImportPreviewRow {
  row_number: number;
  status: AdminPreceptorImportRowStatus;
  can_import: boolean;
  name: string | null;
  primary_email: string | null;
  specialties: string[];
  site_name: string | null;
  site_address: string | null;
  seasons_available: string[];
  payment_note: string | null;
  site_note: string | null;
  license_states: string[];
  license_number: string | null;
  license_expiration_date: string | null;
  license_verification_url: string | null;
  accepts_tele: boolean;
  accepts_in_person?: boolean;
  site_contact_name: string | null;
  site_contact_email: string | null;
  site_contact_phone: string | null;
  availability_notes: string | null;
  /** Seasons the preceptor is marked unavailable for, parsed from the workbook Person - Labels column. */
  parsed_seasons_unavailable?: string[];
  /** University names excluded by "NON-<name> ONLY" / "Non-<name>" labels. Blocklist semantics. */
  parsed_restricted_universities?: string[];
  /** Canonical system label names the parser applied for this row. */
  parsed_applied_label_tokens?: string[];
  /** Workbook label tokens that did not match any backend rule. Surface for admin review. */
  parsed_unmapped_label_tokens?: string[];
  /** Non-null when a modality-bearing label (e.g. "In-Person Only") overrode the Tele-Rotation column. */
  parsed_modality_override?: {
    accepts_tele: boolean;
    accepts_in_person: boolean;
  } | null;
  editable_fields: AdminPreceptorImportEditableFields;
  raw_fields: AdminPreceptorImportRawField[];
  issues: string[];
}

export interface AdminPreceptorImportPreviewResponse {
  preview_token: string;
  workbook_name: string;
  worksheet_name: string;
  expires_at: string;
  summary: {
    total_rows: number;
    ready_rows: number;
    duplicate_rows: number;
    error_rows: number;
  };
  rows: AdminPreceptorImportPreviewRow[];
}

export interface AdminPreceptorImportCommitRow {
  row_number: number;
  preceptor_id?: number;
  name: string | null;
  primary_email: string | null;
  status?: string | null;
  reason?: string | null;
}

export interface AdminPreceptorImportCommitResponse {
  imported_count: number;
  skipped_count: number;
  failed_count: number;
  created: AdminPreceptorImportCommitRow[];
  skipped: AdminPreceptorImportCommitRow[];
  failed: AdminPreceptorImportCommitRow[];
}

export async function admitPendingPreceptor(
  clerkId: string,
  payload: AdminAdmitPendingPreceptorPayload
): Promise<AdminAdmitPendingPreceptorResponse> {
  const res = await axios.post<Result<AdminAdmitPendingPreceptorResponse>>(
    `/admin/preceptors/pending/${encodeURIComponent(clerkId)}/admit`,
    payload
  );
  return res.data.data;
}

export async function createAdminPreceptor(
  payload: AdminCreatePreceptorPayload,
): Promise<BackendPreceptor> {
  return axios
    .post<Result<BackendPreceptor>>(`/admin/preceptors`, payload)
    .then(res => res.data.data);
}

export async function deletePreceptor(id: number | string) {
  const { data } = await axios.delete(`/admin/preceptors/${encodeURIComponent(String(id))}`);
  return data;
}

/**
 * Update the Pipedrive person ID linked to a preceptor's user (admin only).
 * Pass an empty string / null to clear it. Returns the refreshed admin preceptor payload.
 */
export async function updatePreceptorPipedriveId(
  id: number | string,
  pipedrivePersonId: string | null,
): Promise<BackendPreceptor> {
  return axios
    .patch<Result<BackendPreceptor>>(`/admin/preceptors/${encodeURIComponent(String(id))}`, {
      pipedrive_person_id: pipedrivePersonId === '' ? null : pipedrivePersonId,
    })
    .then(res => res.data.data);
}

export async function restorePreceptor(id: number | string) {
  const { data } = await axios.post(`/admin/preceptors/${encodeURIComponent(String(id))}/restore`);
  return data;
}

/**
 * @deprecated Use queueAdminPreceptorImport for async background processing.
 * Kept for backward compatibility but no longer called by the import page.
 */
export async function previewAdminPreceptorImport(
  file: File,
): Promise<AdminPreceptorImportPreviewResponse> {
  const formData = new FormData();
  formData.append('file', file);

  return axios
    .post<Result<AdminPreceptorImportPreviewResponse>>(
      `/admin/preceptors/import/preview`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    .then(res => res.data.data);
}

export async function queueAdminPreceptorImport(file: File): Promise<{ job_id: string }> {
  const formData = new FormData();
  formData.append('file', file);

  return axios
    .post<{ data: { job_id: string } }>(
      `/admin/preceptors/import/preview`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    .then(res => res.data.data);
}

export async function getImportJobStatus(jobId: string): Promise<ImportJobProgress> {
  return axios
    .get<{ data: ImportJobProgress }>(`/admin/preceptors/import/job/${encodeURIComponent(jobId)}`)
    .then(res => res.data.data);
}

export async function getCompletedImportPreview(previewToken: string): Promise<AdminPreceptorImportPreviewResponse> {
  return axios
    .get<Result<AdminPreceptorImportPreviewResponse>>(
      `/admin/preceptors/import/preview/${encodeURIComponent(previewToken)}`,
    )
    .then(res => res.data.data);
}

/**
 * @deprecated Use queueAdminPreceptorImportCommit for async background processing.
 * Kept for backward compatibility.
 */
export async function commitAdminPreceptorImport(payload: {
  preview_token: string;
  selected_rows: number[];
  row_overrides?: Record<number, Partial<AdminPreceptorImportEditableFields>>;
}): Promise<AdminPreceptorImportCommitResponse> {
  return axios
    .post<Result<AdminPreceptorImportCommitResponse>>(`/admin/preceptors/import/commit`, payload)
    .then(res => res.data.data);
}

export async function queueAdminPreceptorImportCommit(payload: {
  preview_token: string;
  selected_rows: number[];
  row_overrides?: Record<number, Partial<AdminPreceptorImportEditableFields>>;
}): Promise<{ job_id: string }> {
  return axios
    .post<{ data: { job_id: string } }>(`/admin/preceptors/import/commit`, payload)
    .then(res => res.data.data);
}

export async function getImportCommitJobStatus(jobId: string): Promise<CommitJobProgress> {
  return axios
    .get<{ data: CommitJobProgress }>(`/admin/preceptors/import/commit/job/${encodeURIComponent(jobId)}`)
    .then(res => res.data.data);
}

export interface AdditionalProfileRequestSummary {
  id: number;
  status: 'pending' | 'approved' | 'rejected';
  decision_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
  decided_at: string | null;
}

const PROFILE_REQUEST_STATUSES = ['pending', 'approved', 'rejected'] as const;

export type AdminProfileRequestStatus = (typeof PROFILE_REQUEST_STATUSES)[number];

export interface AdminProfileRequestRequester {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  multi_profile_enabled_at?: string | null;
}

export interface AdminProfileRequestPreceptor {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  status?: string | null;
}

export interface AdminProfileRequest {
  id: number;
  user_id?: number;
  preceptor_id: number | null;
  status: AdminProfileRequestStatus;
  payload: Record<string, unknown> | null;
  decision_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
  decided_at: string | null;
  requester: AdminProfileRequestRequester | null;
  preceptor: AdminProfileRequestPreceptor | null;
}

export interface AdminProfileRequestPage {
  data: AdminProfileRequest[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  next_page_url: string | null;
  prev_page_url: string | null;
}

export interface PreceptorMultiProfileAccess {
  can_create: boolean;
  can_request: boolean;
  pending_request: AdditionalProfileRequestSummary | null;
  latest_request: AdditionalProfileRequestSummary | null;
  enabled_at?: string | null;
}

export interface ContactPayload {
  value: string;
  label?: string | null;
}

export interface AcceptedProgramPayload {
  program_id: string;
  sub_programs: string[];
}

export interface PreceptorProfilePayload {
  status: 'draft' | 'pending';
  first_name: string;
  last_name: string;
  titles?: string[];
  bio?: string | null;
  specialty_id?: number | null;
  specialty_ids?: number[] | null;
  graduate_school?: string | null;
  graduation_year?: number | null;
  preceptor_track?: "np" | "pa" | "img" | null;
  rotation_setting?: string | null;
  seasons_available?: string[] | null;
  seasons_unavailable?: string[] | null;
  accepted_program_types?: string[];
  accepted_programs?: AcceptedProgramPayload[];
  license_number?: string | null;
  license_state?: string | null;
  license_states?: string[] | null;
  license_verification_url?: string | null;
  years_experience?: number | null;
  availability_notes?: string | null;
  accepts_tele?: boolean;
  accepts_in_person?: boolean;
  honorarium_to_pay?: number | null;
  honorarium_speed?: string | null;
  payment_method?: string | null;
  payment_schedule?: string | null;
  payment_instruction?: string | null;
  payment_details?: string | null;
  email_contacts?: ContactPayload[];
  phone_contacts?: ContactPayload[];
}

function toOptionalString(value: unknown): string | null {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toOptionalNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normalizeAdminProfileRequest(raw: unknown): AdminProfileRequest {
  const record = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};

  const idValue = toOptionalNumber(record.id) ?? 0;
  const userIdValue = toOptionalNumber(record.user_id);
  const preceptorIdValue = record.preceptor_id === null || record.preceptor_id === undefined
    ? null
    : toOptionalNumber(record.preceptor_id);

  const statusValue = String(record.status ?? 'pending').toLowerCase();
  const status = PROFILE_REQUEST_STATUSES.includes(statusValue as AdminProfileRequestStatus)
    ? (statusValue as AdminProfileRequestStatus)
    : 'pending';

  const payloadRaw = record.payload;
  const payload = payloadRaw && typeof payloadRaw === 'object' && !Array.isArray(payloadRaw)
    ? (payloadRaw as Record<string, unknown>)
    : null;

  const requesterRaw = record.requester;
  let requester: AdminProfileRequest['requester'] = null;
  if (requesterRaw && typeof requesterRaw === 'object') {
    const data = requesterRaw as Record<string, unknown>;
    const requesterId = toOptionalNumber(data.id) ?? 0;
    requester = {
      id: requesterId,
      firstName: (toOptionalString(data.firstName ?? data.first_name)) ?? undefined,
      lastName: (toOptionalString(data.lastName ?? data.last_name)) ?? undefined,
      email: (toOptionalString(data.email)) ?? undefined,
      multi_profile_enabled_at: toOptionalString(data.multi_profile_enabled_at),
    };
  }

  const preceptorRaw = record.preceptor;
  let preceptor: AdminProfileRequest['preceptor'] = null;
  if (preceptorRaw && typeof preceptorRaw === 'object') {
    const data = preceptorRaw as Record<string, unknown>;
    const preceptorId = toOptionalNumber(data.id) ?? 0;
    preceptor = {
      id: preceptorId,
      first_name: (toOptionalString(data.first_name ?? data.firstName)) ?? undefined,
      last_name: (toOptionalString(data.last_name ?? data.lastName)) ?? undefined,
      status: (toOptionalString(data.status)) ?? undefined,
    };
  }

  return {
    id: idValue,
    user_id: userIdValue ?? undefined,
    preceptor_id: preceptorIdValue ?? null,
    status,
    payload,
    decision_reason: toOptionalString(record.decision_reason),
    created_at: toOptionalString(record.created_at),
    updated_at: toOptionalString(record.updated_at),
    decided_at: toOptionalString(record.decided_at),
    requester,
    preceptor,
  };
}

export interface PreceptorProfilesResponse {
  active_preceptor_id: number | null;
  default_preceptor_id: number | null;
  profiles: PreceptorProfile[];
  multi_profile: PreceptorMultiProfileAccess;
}

export interface SetActivePreceptorResponse {
  active_preceptor_id: number | null;
  default_preceptor_id: number | null;
  active_profile: PreceptorProfile;
  previous_profile: {
    id: number;
    last_active: string | null;
  } | null;
}

export async function getMyPreceptorProfiles(): Promise<PreceptorProfilesResponse> {
  return axios.get<Result<PreceptorProfilesResponse>>(`/preceptors/me`).then(res => res.data.data);
}

export async function requestAdditionalPreceptorProfile(reason: string): Promise<AdditionalProfileRequestSummary> {
  return axios
    .post<Result<Record<string, unknown>>>(`/preceptor-profile-requests`, { reason })
    .then(res => {
      const payload = res.data.data as Record<string, unknown>;
      const idValue = payload.id;
      return {
        id: typeof idValue === 'number' ? idValue : Number(idValue ?? 0),
        status: (payload.status as AdditionalProfileRequestSummary['status']) ?? 'pending',
        decision_reason: (payload.decision_reason as string | null) ?? null,
        created_at: (payload.created_at as string | null) ?? null,
        updated_at: (payload.updated_at as string | null) ?? null,
        decided_at: (payload.decided_at as string | null) ?? null,
      } satisfies AdditionalProfileRequestSummary;
    });
}

export async function getAdminPreceptorProfileRequests(params: {
  status?: AdminProfileRequestStatus | 'all';
  page?: number;
} = {}): Promise<AdminProfileRequestPage> {
  const query: Record<string, unknown> = {};
  if (params.page && params.page > 0) {
    query.page = params.page;
  }
  if (params.status && params.status !== 'all') {
    query.status = params.status;
  }

  return axios
    .get<Result<Record<string, unknown>>>(`/admin/preceptor-profile-requests`, { params: query })
    .then(res => {
      const root = (res.data.data ?? {}) as Record<string, unknown>;
      const requestsPayload = root.requests && typeof root.requests === 'object'
        ? (root.requests as Record<string, unknown>)
        : {};
      const rawData = Array.isArray(requestsPayload.data) ? requestsPayload.data : [];
      const data = rawData.map(normalizeAdminProfileRequest);

      return {
        data,
        current_page: toOptionalNumber(requestsPayload.current_page) ?? 1,
        per_page: toOptionalNumber(requestsPayload.per_page) ?? data.length,
        total: toOptionalNumber(requestsPayload.total) ?? data.length,
        last_page: toOptionalNumber(requestsPayload.last_page) ?? 1,
        next_page_url: typeof requestsPayload.next_page_url === 'string' ? requestsPayload.next_page_url : null,
        prev_page_url: typeof requestsPayload.prev_page_url === 'string' ? requestsPayload.prev_page_url : null,
      } satisfies AdminProfileRequestPage;
    });
}

export async function approveAdminPreceptorProfileRequest(id: number, reason?: string): Promise<AdminProfileRequest> {
  const trimmed = reason?.trim();
  const payload = trimmed ? { reason: trimmed } : {};

  return axios
    .post<Result<Record<string, unknown>>>(`/admin/preceptor-profile-requests/${encodeURIComponent(id)}/approve`, payload)
    .then(res => normalizeAdminProfileRequest(res.data.data));
}

export async function rejectAdminPreceptorProfileRequest(id: number, reason: string): Promise<AdminProfileRequest> {
  const trimmed = reason.trim();
  if (!trimmed) {
    return Promise.reject(new Error('Rejection reason is required.'));
  }

  return axios
    .post<Result<Record<string, unknown>>>(`/admin/preceptor-profile-requests/${encodeURIComponent(id)}/reject`, { reason: trimmed })
    .then(res => normalizeAdminProfileRequest(res.data.data));
}

export async function setDefaultPreceptorProfile(id: number) {
  return axios.post<Result<PreceptorProfile>>(`/preceptors/${encodeURIComponent(id)}/default`).then(res => res.data.data);
}

export async function setActivePreceptorProfile(id: number): Promise<SetActivePreceptorResponse> {
  return axios
    .post<Result<SetActivePreceptorResponse>>(`/preceptors/${encodeURIComponent(id)}/active`)
    .then(res => res.data.data);
}

export async function updateMe(data: Partial<PreceptorProfile>) {
  return axios.put<Result<PreceptorProfile>>(`/preceptors/me`, data).then(res => res.data.data);
}

export async function createPreceptorProfile(payload: PreceptorProfilePayload): Promise<PreceptorProfile> {
  return axios.post<Result<PreceptorProfile>>(`/preceptors`, payload).then(res => res.data.data);
}

export async function updatePreceptorProfile(id: number, payload: PreceptorProfilePayload): Promise<PreceptorProfile> {
  return axios.patch<Result<PreceptorProfile>>(`/preceptors/${encodeURIComponent(id)}`, payload).then(res => res.data.data);
}

export interface PreceptorSiteUpsert extends Omit<PreceptorSiteAssignment, 'site'> {
  site_id: number;
  schedules: PreceptorSiteSchedule[];
}

export async function getMyPreceptorSites(): Promise<PreceptorSiteAssignment[]> {
  return axios.get<Result<PreceptorSiteAssignment[]>>(`/preceptors/me/sites`).then(res => res.data.data ?? []);
}

export async function saveMyPreceptorSites(sites: PreceptorSiteUpsert[]): Promise<PreceptorSiteAssignment[]> {
  return axios.put<Result<PreceptorSiteAssignment[]>>(`/preceptors/me/sites`, { sites }).then(res => res.data.data ?? []);
}

export async function getAdminPreceptorSites(preceptorId: number | string): Promise<PreceptorSiteAssignment[]> {
  return axios
    .get<Result<PreceptorSiteAssignment[]>>(`/admin/preceptors/${encodeURIComponent(preceptorId)}/sites`)
    .then(res => res.data.data ?? []);
}

export async function saveAdminPreceptorSites(
  preceptorId: number | string,
  sites: PreceptorSiteUpsert[],
): Promise<PreceptorSiteAssignment[]> {
  return axios
    .put<Result<PreceptorSiteAssignment[]>>(`/admin/preceptors/${encodeURIComponent(preceptorId)}/sites`, { sites })
    .then(res => res.data.data ?? []);
}

export interface FindOrCreateSitePayload {
  name: string;
  type?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  public_latitude?: number | null;
  public_longitude?: number | null;
}

export async function findOrCreateSite(payload: FindOrCreateSitePayload): Promise<Site> {
  return axios.post<Result<Site>>(`/sites/find-or-create`, payload).then(res => res.data.data);
}

export interface UpdateSitePayload {
  name?: string;
  type?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  public_latitude?: number | null;
  public_longitude?: number | null;
}

export async function updateAdminSite(siteId: number | string, payload: UpdateSitePayload): Promise<Site> {
  return axios
    .put<Result<Site>>(`/admin/sites/${encodeURIComponent(siteId)}`, payload)
    .then(res => res.data.data);
}


export async function getSpecialties() {
  return axios.get<Result<Specialty[]>>(`/public/specialties`).then(res => res.data.data);
}

/**
 * Distinct site-state values actually present among preceptors, used to build
 * the State filter dropdown so it only offers states that exist in the data
 * (and matches the exact stored value, whether a code like "MD" or a full name).
 */
export async function getPreceptorStates(): Promise<string[]> {
  const res = await axios.get<{ data: string[] }>(`/admin/preceptors/states`);
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

export async function getLabels() {
  return axios.get<Result<Label[]>>(`/public/labels`).then(res => res.data.data);
}

export async function getSites() {
  return axios.get<Result<Site[]>>(`/public/sites`).then(res => res.data.data);
}

export interface Site {
  id: number;              // Optional: usually returned by backend
  user_id?: number;
  name: string;
  type: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  is_active: boolean;
  latitude?: number;
  longitude?: number;
  created_at?: string;      // Optional timestamps
  updated_at?: string;
  public_location?: {
    latitude: number | string;
    longitude: number | string;
  } | null;
}


export interface Specialty {
  id: number | string;
  name: string;
}

export async function getPreceptorBookings(): Promise<PreceptorBooking[]> {
  try {
    const response = await axios.get<Result<PreceptorBooking[]>>(`/bookings/preceptor`);
    return response.data.data || [];
  } catch (error) {
    console.error('Failed to fetch preceptor bookings:', error);
    return [];
  }
}

export async function getPreceptorBookingDetail(id: string | number): Promise<PreceptorBookingDetail | null> {
  try {
    const response = await axios.get<Result<PreceptorBookingDetail>>(`/bookings/${encodeURIComponent(id)}`);
    return response.data.data || null;
  } catch (error) {
    console.error('Failed to fetch preceptor booking detail:', error);
    return null;
  }
}

export async function checkPreceptorApplicationEmail(
  email: string,
  options?: { signal?: AbortSignal }
): Promise<{ email: string; exists: boolean }> {
  const response = await axios.post<Result<{ email: string; exists: boolean }>>(
    `/preceptor-applications/check-email`,
    { email },
    { signal: options?.signal }
  );

  return response.data.data;
}

export async function getPreceptorDocumentsById(preceptorId: number | string): Promise<import('@/types/preceptor').PreceptorDocument[]> {
  const response = await axios.get<Result<import('@/types/preceptor').PreceptorDocument[]>>(`/preceptors/${encodeURIComponent(String(preceptorId))}/documents`);
  return response.data.data ?? [];
}

export async function createPreceptorDocumentForPreceptor(
  preceptorId: number | string,
  data: {
    name: string;
    file_url?: string;
    cloudinary_url?: string;
    cloudinary_public_id?: string;
    cloudinary_format?: string;
    cloudinary_original_filename?: string;
  }
): Promise<import('@/types/preceptor').PreceptorDocument> {
  const response = await axios.post<Result<import('@/types/preceptor').PreceptorDocument>>(
    `/preceptors/${encodeURIComponent(String(preceptorId))}/documents`,
    data
  );
  return response.data.data;
}

export async function deletePreceptorDocumentForPreceptor(
  preceptorId: number | string,
  documentId: number
): Promise<void> {
  await axios.delete(`/preceptors/${encodeURIComponent(String(preceptorId))}/documents/${documentId}`);
}

export async function getPreceptorBookingDocuments(
  bookingId: string | number
): Promise<import('@/types/api/booking').BookingDocument[]> {
  try {
    const response = await axios.get<Result<PreceptorBookingDetail>>(
      `/bookings/${encodeURIComponent(String(bookingId))}?with_documents=true`
    );
    const data = response.data.data;
    return Array.isArray(data?.documents) ? data.documents : [];
  } catch (error) {
    console.error('Failed to fetch preceptor booking documents:', error);
    return [];
  }
}

export interface UnsignedDocumentGroup {
  booking_id: number;
  booking_number: string | null;
  student_name: string | null;
  documents: Array<{
    document_id: number;
    document_title: string;
    pivot_id: number | null;
  }>;
}

export interface UnsignedDocumentsResponse {
  data: UnsignedDocumentGroup[];
  total_unsigned: number;
}

export async function getPreceptorUnsignedDocuments(): Promise<UnsignedDocumentsResponse> {
  try {
    const response = await axios.get<{ success: boolean; data: UnsignedDocumentGroup[]; total_unsigned: number }>(
      `/bookings/preceptor/unsigned-documents`
    );
    return {
      data: response.data.data || [],
      total_unsigned: response.data.total_unsigned || 0,
    };
  } catch (error) {
    console.error('Failed to fetch preceptor unsigned documents:', error);
    return { data: [], total_unsigned: 0 };
  }
}
